import { useStore } from "zustand"
import { createStore } from "zustand/vanilla"

/**
 * Admin View Mode Store
 *
 * Allows admins to temporarily view the app as a regular member for testing/dev.
 * This only affects the frontend display - API permissions remain unchanged.
 */

type AdminViewState = {
  /** When true, admin sees the member-only view */
  viewAsMember: boolean
}

const STORAGE_KEY = "beagle:admin-view-mode"

const getInitialState = (): AdminViewState => {
  if (typeof window === "undefined") {
    return { viewAsMember: false }
  }

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored === "member") {
      return { viewAsMember: true }
    }
  } catch {
    // sessionStorage not available
  }

  return { viewAsMember: false }
}

export const adminViewStore = createStore<AdminViewState>(getInitialState)

/**
 * Toggle between admin and member view.
 * Persists to sessionStorage (cleared when browser closes).
 */
export const toggleAdminViewMode = (): void => {
  const current = adminViewStore.getState().viewAsMember
  const next = !current

  adminViewStore.setState({ viewAsMember: next })

  try {
    if (next) {
      sessionStorage.setItem(STORAGE_KEY, "member")
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // sessionStorage not available
  }
}

/**
 * Reset to normal admin view.
 */
export const resetAdminViewMode = (): void => {
  adminViewStore.setState({ viewAsMember: false })

  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // sessionStorage not available
  }
}

/**
 * Hook to check if admin should see member-only view.
 * Returns false if user is not an admin (no effect for regular members).
 */
export const useViewAsMember = (): boolean => {
  return useStore(adminViewStore, (state) => state.viewAsMember)
}

/**
 * Hook that returns the effective isAdmin value.
 * If viewAsMember is active, returns false even for admins.
 */
export const useEffectiveIsAdmin = (actualIsAdmin: boolean): boolean => {
  const viewAsMember = useViewAsMember()
  return actualIsAdmin && !viewAsMember
}
